import { useRef, useState } from "react";
import { computeSnap } from "@/lib/snap";
import { Rnd } from "react-rnd";
import { ElementView } from "@/components/card/ElementView";
import { SideBackground } from "@/components/card/CardSide";

const r2 = (n) => Math.round(n * 100) / 100;

const Guides = ({ tpl, scale }) => {
  const safe = 3 * scale;
  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex: 9999 }}>
      <div className="absolute border border-dashed border-sky-400/70" style={{ inset: safe }} />
      <div className="absolute top-0 bottom-0 border-l border-dashed border-fuchsia-400/40" style={{ left: (tpl.width / 2) * scale }} />
      <div className="absolute left-0 right-0 border-t border-dashed border-fuchsia-400/40" style={{ top: (tpl.height / 2) * scale }} />
    </div>
  );
};

function CanvasElement({ el, idx, scale, selected, setSel, updateEl, grid, snapTo, setLines }) {
  const changed = (a, b) => Math.abs(a - b) > 0.01;
  return (
    <Rnd
      size={{ width: el.w * scale, height: el.h * scale }}
      position={{ x: el.x * scale, y: el.y * scale }}
      style={{ zIndex: idx + 1 }}
      disableDragging={el.locked}
      enableResizing={!el.locked && selected}
      dragGrid={grid}
      resizeGrid={grid}
      lockAspectRatio={el.type === "qr"}
      minWidth={2}
      minHeight={1}
      onMouseDown={(e) => { e.stopPropagation(); setSel(el.id); }}
      onDrag={(e, d) => setLines(snapTo(el, d.x / scale, d.y / scale).lines)}
      onDragStop={(e, d) => {
        setLines([]);
        const { x, y } = snapTo(el, d.x / scale, d.y / scale);
        if (changed(x, el.x) || changed(y, el.y)) updateEl(el.id, { x, y });
      }}
      onResizeStop={(e, dir, ref, delta, pos) => updateEl(el.id, {
        w: r2(ref.offsetWidth / scale), h: r2(ref.offsetHeight / scale), x: r2(pos.x / scale), y: r2(pos.y / scale),
      })}
      resizeHandleClasses={selected ? { topLeft: "rh", topRight: "rh", bottomLeft: "rh", bottomRight: "rh", top: "rh-e", bottom: "rh-e", left: "rh-e", right: "rh-e" } : {}}
      className={selected ? "cf-selected" : "cf-hoverable"}
    >
      <div data-testid={`canvas-element-${el.id}`} style={{ width: "100%", height: "100%", transform: `rotate(${el.rotation || 0}deg)`, opacity: el.opacity ?? 1 }}>
        <ElementView el={el} scale={scale} editing />
      </div>
    </Rnd>
  );
}

export default function CanvasStage({ tpl, side, scale, sel, setSel, updateEl, snap, guides, smart }) {
  const ref = useRef(null);
  const s = tpl[side];
  const grid = snap ? [0.5 * scale, 0.5 * scale] : undefined;
  const [lines, setLines] = useState([]);
  const snapTo = (el, x, y) => (smart
    ? computeSnap(el, x, y, s.elements.filter((o) => o.id !== el.id && !o.hidden), tpl, 6 / scale)
    : { x: r2(x), y: r2(y), lines: [] });
  return (
    <main className="flex-1 min-w-0 overflow-auto bg-slate-950 studio-grid relative" onMouseDown={() => setSel(null)} data-testid="canvas-workspace">
      <div className="min-h-full min-w-max flex flex-col items-center justify-center p-16 gap-4">
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.2em]" data-testid="canvas-side-label">{side} · {tpl.width} × {tpl.height} mm</div>
        <div
          ref={ref}
          data-testid="canvas-drop-area"
          className="relative shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] ring-1 ring-slate-700"
          style={{ width: tpl.width * scale, height: tpl.height * scale, background: s.background || "#fff", borderRadius: 3.18 * scale, overflow: "hidden" }}
        >
          <SideBackground side={s} />
          {s.elements.map((el, i) => el.hidden ? null : (
            <CanvasElement key={el.id} el={el} idx={i} scale={scale} selected={sel === el.id} setSel={setSel} updateEl={updateEl} grid={grid} snapTo={snapTo} setLines={setLines} />
          ))}
          {guides && <Guides tpl={tpl} scale={scale} />}
          {lines.map((l, i) => (
            <div key={i} data-testid={`smart-guide-${l.type}`} className="pointer-events-none absolute bg-rose-500"
              style={l.type === "v" ? { left: l.pos * scale - 0.5, top: 0, bottom: 0, width: 1, zIndex: 10000 } : { top: l.pos * scale - 0.5, left: 0, right: 0, height: 1, zIndex: 10000 }} />
          ))}
        </div>
        <p className="text-[11px] text-slate-500">Drag to move · handles to resize · arrows nudge 0.5mm (Shift = 2mm) · Del removes · Ctrl+D duplicates</p>
      </div>
    </main>
  );
}
